import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { AreaId } from './types/pulse';
import { followUpResult, followUps, openQuestion, openResult, opening, ranking, teamRating, techRating } from './data/pulse';
import { usePulse } from './hooks/usePulse';
import { PulseFrame } from './components/pulse/PulseFrame';
import { DataOverlay } from './components/pulse/DataOverlay';
import { StrategyOverlay } from './components/pulse/StrategyOverlay';
import { StoryboardOverlay } from './components/pulse/StoryboardOverlay';
import { OpeningScreen } from './components/pulse/screens/OpeningScreen';
import { RankScreen } from './components/pulse/screens/RankScreen';
import { RankResultScreen } from './components/pulse/screens/RankResultScreen';
import { FollowUpScreen } from './components/pulse/screens/FollowUpScreen';
import { FollowUpResultScreen } from './components/pulse/screens/FollowUpResultScreen';
import { RatingScreen } from './components/pulse/screens/RatingScreen';
import { RatingResultScreen } from './components/pulse/screens/RatingResultScreen';
import { OpenScreen } from './components/pulse/screens/OpenScreen';
import { OpenResultScreen } from './components/pulse/screens/OpenResultScreen';
import { SnapshotScreen } from './components/pulse/screens/SnapshotScreen';
type StepId = 'opening' | 'q1' | 'q1r' | 'q2' | 'q2r' | 'q3' | 'q3r' | 'q4' | 'q4r' | 'q5' | 'q5r' | 'snapshot';
const STEPS: StepId[] = ['opening', 'q1', 'q1r', 'q2', 'q2r', 'q3', 'q3r', 'q4', 'q4r', 'q5', 'q5r', 'snapshot'];
const QUESTION_OF: Record<StepId, number> = {
  opening: 0,
  q1: 1,
  q1r: 1,
  q2: 2,
  q2r: 2,
  q3: 3,
  q3r: 3,
  q4: 4,
  q4r: 4,
  q5: 5,
  q5r: 5,
  snapshot: 6
};
const EASE = [0.23, 1, 0.32, 1] as const;
interface AppProps {
  /** Internal/AbbVie build — adds the AI interpretation to the live results. */
  internalView?: boolean;
}
export function App({
  internalView = true
}: AppProps) {
  const pulse = usePulse();
  const {
    response,
    aggregates
  } = pulse;
  const [index, setIndex] = useState(0);
  const [rankDraft, setRankDraft] = useState<(AreaId | null)[]>([null, null, null]);
  const [choiceDraft, setChoiceDraft] = useState<string | null>(null);
  const [freeDraft, setFreeDraft] = useState('');
  const [teamDraft, setTeamDraft] = useState<number | null>(null);
  const [techDraft, setTechDraft] = useState<number | null>(null);
  const [openDraft, setOpenDraft] = useState('');
  const [dataOpen, setDataOpen] = useState(false);
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [storyboardOpen, setStoryboardOpen] = useState(false);
  const step = STEPS[index];
  const area: AreaId = response.topArea ?? 'diagnosis';
  const freeSelected = choiceDraft ? followUps[area].options.find(o => o.id === choiceDraft)?.free === true : false;
  const go = (delta: number) => setIndex(i => Math.min(STEPS.length - 1, Math.max(0, i + delta)));
  const restart = () => {
    pulse.reset();
    setRankDraft([null, null, null]);
    setChoiceDraft(null);
    setFreeDraft('');
    setTeamDraft(null);
    setTechDraft(null);
    setOpenDraft('');
    setIndex(0);
  };
  const commitAndAdvance = () => {
    if (step === 'q1') {
      // A different #1 area routes to a different follow-up question.
      if (rankDraft[0] !== response.topArea) {
        setChoiceDraft(null);
        setFreeDraft('');
      }
      pulse.submitRanking(rankDraft.filter((a): a is AreaId => a !== null));
    }
    if (step === 'q2' && choiceDraft) pulse.submitFollowUp(choiceDraft, freeDraft);
    if (step === 'q3' && teamDraft !== null) pulse.submitRating('team', teamDraft);
    if (step === 'q4' && techDraft !== null) pulse.submitRating('tech', techDraft);
    if (step === 'q5') pulse.submitOpen(openDraft);
    go(1);
  };
  const nextLabel: Record<StepId, string> = {
    opening: opening.cta,
    q1: ranking.cta,
    q1r: ranking.resultCta,
    q2: followUps[area].cta,
    q2r: followUpResult.cta,
    q3: teamRating.cta,
    q3r: teamRating.resultCta,
    q4: techRating.cta,
    q4r: techRating.resultCta,
    q5: openQuestion.cta,
    q5r: openResult.cta,
    snapshot: ''
  };
  const nextDisabled = step === 'q1' && rankDraft.some(a => a === null) || step === 'q2' && (!choiceDraft || freeSelected && !freeDraft.trim()) || step === 'q3' && teamDraft === null || step === 'q4' && techDraft === null || step === 'q5' && !openDraft.trim();
  const footnote = step === 'q5' ? 'Stored anonymously with a timestamp. No name, email or hospital is collected.' : undefined;
  return <div className="min-h-full w-full bg-mist font-sans" data-id="element-0" data-mp="zw0mxbljm0eb">
      <main className="app-shell" data-id="element-1" data-mp="vx06u0ug9bv0">
        <PulseFrame question={QUESTION_OF[step]} totalQuestions={5} footnote={footnote} onBack={index > 0 && step !== 'snapshot' ? () => go(-1) : undefined} onNext={step === 'snapshot' ? undefined : commitAndAdvance} nextLabel={nextLabel[step]} nextDisabled={nextDisabled} onRestart={step === 'opening' ? undefined : restart} onOpenData={() => setDataOpen(true)} onOpenStrategy={internalView ? () => setStrategyOpen(true) : undefined} onOpenStoryboard={internalView ? () => setStoryboardOpen(true) : undefined} data-id="element-2" data-mp="vvp6yc44ko96">
          <AnimatePresence mode="wait" data-id="element-3" data-mp="2euwswj70pr47">
            <motion.div key={step} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease: EASE
          }} className="flex flex-1 flex-col" data-id="element-4" data-mp="2etv5df8tfc5l">
              {step === 'opening' && <OpeningScreen data-id="element-5" data-mp="2su5x0khy8urt" />}

              {step === 'q1' && <RankScreen value={rankDraft} onChange={setRankDraft} data-id="element-6" data-mp="thtq9ufo07c5" />}

              {step === 'q1r' && response.topArea && <RankResultScreen topArea={response.topArea} aggregates={aggregates} data-id="element-7" data-mp="yhprxn0w3moj" />}

              {step === 'q2' && <FollowUpScreen area={area} selected={choiceDraft} onSelect={setChoiceDraft} freeText={freeDraft} onFreeText={setFreeDraft} data-id="element-8" data-mp="21tojc52kt18v" />}

              {step === 'q2r' && response.followUpChoice && <FollowUpResultScreen area={area} choice={response.followUpChoice} freeText={response.followUpFreeText} aggregates={aggregates} data-id="element-9" data-mp="2kq6hp5bxjze3" />}

              {step === 'q3' && <RatingScreen block={teamRating} value={teamDraft} onChange={setTeamDraft} data-id="element-10" data-mp="2ph50cbke59ki" />}

              {step === 'q3r' && response.teamRating !== null && <RatingResultScreen value={response.teamRating} tally={aggregates.team} context="Better use of the wider eye-care team" data-id="element-11" data-mp="33h7i7xsp82zr" />}

              {step === 'q4' && <RatingScreen block={techRating} value={techDraft} onChange={setTechDraft} data-id="element-12" data-mp="3n1ofhaaa0wi9" />}

              {step === 'q4r' && response.techRating !== null && <RatingResultScreen value={response.techRating} tally={aggregates.tech} context="Potential of technology to free specialist time" data-id="element-13" data-mp="3d4a2jknkapup" />}

              {step === 'q5' && <OpenScreen value={openDraft} onChange={setOpenDraft} aggregates={aggregates} data-id="element-14" data-mp="345okdqm05cug" />}

              {step === 'q5r' && <OpenResultScreen aggregates={aggregates} answer={response.openResponse} data-id="element-15" data-mp="2uf3owc1mkofr" />}

              {step === 'snapshot' && <SnapshotScreen aggregates={aggregates} onRestart={restart} onOpenData={() => setDataOpen(true)} data-id="element-16" data-mp="2ggn6voybbbib" />}
            </motion.div>
          </AnimatePresence>
        </PulseFrame>
      </main>

      <AnimatePresence data-id="element-17" data-mp="2vx289t9kyejq">
        {dataOpen && <DataOverlay aggregates={aggregates} onClose={() => setDataOpen(false)} internalView={internalView} data-id="element-18" data-mp="2qwoad41p0kzf" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-19" data-mp="2hw9g3f6r3vjs">
        {strategyOpen && <StrategyOverlay onClose={() => setStrategyOpen(false)} data-id="element-20" data-mp="2hy2l6q1pu7po" />}
      </AnimatePresence>

      <AnimatePresence data-id="element-21" data-mp="2d513kaa4ju2e">
        {storyboardOpen && <StoryboardOverlay onClose={() => setStoryboardOpen(false)} data-id="element-22" data-mp="2d3ygkdfxabrr" />}
      </AnimatePresence>
    </div>;
}